import React from "react";
import { Page } from "../../../activities/AppPage";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import CareMomentsIntroBottomSheet from "../../CareMomentsIntro/components/CareMomentsIntroBottomSheet";
import { UseFinishOnboardingReturnType } from "../../FinishOnboardingSection";
import { usePinUnlockVisibilityValue } from "../../PinUnlockScreen/usePinUnlockVisibility";
import useFlag from "../hooks/useFlag";
import { MainTab } from "../MainV2Screen";
import FinishOnboardingModals from "./FinishOnboardingModals";

type OverlayType =
  | { name: "Promo" }
  | { name: "CareMomentsIntro" }
  | {
      name: "Onboarding";
      data: { type: React.ComponentPropsWithoutRef<typeof FinishOnboardingModals>["type"] };
    };

type Props = {
  onboardingStatus: UseFinishOnboardingReturnType;
  caregiver: AppCaregiver;
  mainTab: MainTab;
  isPromoOpen: boolean;
  onRequestNavigate: (page: Page) => void;
};

/**
 * This component is responsible for showing and prioritizing the overlays of the main screen.
 */
export default function MainOverlays(props: Props) {
  // While shownOverlay could be computed, we don't want to show overlays in sequence.
  // We only want to show the first overlay and be done with it.
  const [shownOverlay, setShownOverlay] = React.useState<OverlayType | null>(null);

  const isPinUnlockOpen = usePinUnlockVisibilityValue();
  const careMomentsFlag = useFlag({ name: "OpenedCareMomentsIntro" });

  React.useEffect(() => {
    if (isPinUnlockOpen || props.isPromoOpen) {
      return;
    }

    if (shownOverlay !== null) {
      return;
    }

    if (!careMomentsFlag.isFlagged && props.caregiver.shouldShowCareMoments) {
      setShownOverlay({ name: "CareMomentsIntro" });
      return;
    }

    if (props.onboardingStatus.show) {
      setShownOverlay({ name: "Onboarding", data: { type: props.onboardingStatus.type } });
      return;
    }
  }, [
    careMomentsFlag.isFlagged,
    isPinUnlockOpen,
    props.caregiver.shouldShowCareMoments,
    props.isPromoOpen,
    props.onboardingStatus.show,
    props.onboardingStatus.type,
    shownOverlay,
  ]);

  if (shownOverlay === null) {
    return <></>;
  }

  switch (shownOverlay.name) {
    case "Promo":
      // already shown early
      return <></>;

    case "CareMomentsIntro":
      return (
        <CareMomentsIntroBottomSheet
          defaultOpen={true}
          onClick={() => props.onRequestNavigate({ type: "CareMomentsIntro" })}
        />
      );

    case "Onboarding":
      return (
        <FinishOnboardingModals
          caregiver={props.caregiver}
          hideModal={props.mainTab !== "explore"}
          type={shownOverlay.data.type}
          onClick={() => props.onRequestNavigate({ type: "OnboardingActivity" })}
        />
      );
  }
}
